import React, { useState, useEffect, useRef } from "react";
import {
  FaTools,
  FaList,
  FaUserTie,
  FaIndustry,
  FaWarehouse,
  FaBuilding,
} from "react-icons/fa";
import {
  useMoveDeviceToProductionMutation,
  useMoveDeviceToStorageMutation as useStorageMoveToStorageMutation,
  useResetOperationQuotaMutation,
  useToggleDeviceActivityMutation as useStorageToggleMutation,
  useMoveDeviceToPlaymakerMutation as useStorageMoveToPlaymakerMutation,
  useMoveDeviceToServiceMutation as useStorageMoveToServiceMutation,
  useMoveDeviceToFirmMutation as useStorageMoveToFirmMutation,
} from "../../store/storageRoles/devicesApi";
import {
  useDealerToggleDeviceActivityMutation,
  useDealerMoveDeviceToFirmMutation,
  useDealerMoveDeviceToPlaymakerMutation,
  useDealerMoveDeviceToServiceMutation,
  useDealerMoveDeviceToStorageMutation,
} from "../../store/dealerRoles/devicesApi";
import { useFetchPlayMakerQuery } from "../../store/storageRoles/usersApi";
import { useFetchDealerPlayMakerQuery } from "../../store/dealerRoles/devicesApi";
import { useFirmData } from "../../Hooks/useFirmData";
import useAuth from "../../Hooks/useAuth";
import { Alert } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { Pagination } from "../atoms";
import { useDebounce } from "../../Hooks/useDebounce";
import { getDeviceDisplayName, dealerHasSubDealers } from "../../utils/deviceUtils";
import { ListSkeleton, EmptyState, ListView, ListToolbar } from "../shared";

import DeviceCard from "./ServiceDevicesList/DeviceCard";
import ConfirmationModal from "./ServiceDevicesList/ConfirmationModal";
import OperationDrawer from "./OperationDrawer";
import CenterDealerMoveDrawer from "./storageRoles/CenterDealerMoveDrawer";
import CenterDealerQuotaDrawer from "./storageRoles/CenterDealerQuotaDrawer";
import usePermission from "../../Hooks/usePermission";
import useFirmQuotaDistribution from "../../Hooks/storageRoles/useFirmQuotaDistribution";
import { DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE, DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN } from "../../utils/deviceActivityLabels";

const FILTER_BUTTONS = [
  { id: "all", label: "Tümü", icon: FaList },
  { id: "storage", label: "Depo", icon: FaWarehouse },
  { id: "firm", label: "Firma", icon: FaBuilding },
  { id: "service", label: "Servis", icon: FaTools },
  { id: "sales", label: "Plasiyer", icon: FaUserTie },
  { id: "production", label: "Üretim", icon: FaIndustry },
];

// Dealer için Üretim yok
const DEALER_FILTER_BUTTONS = [
  { id: "all", label: "Tümü", icon: FaList },
  { id: "storage", label: "Depo", icon: FaWarehouse },
  { id: "firm", label: "Firma", icon: FaBuilding },
  { id: "service", label: "Servis", icon: FaTools },
  { id: "sales", label: "Plasiyer", icon: FaUserTie },
];

const STATUS_NAME_MAP = {
  storage: "Depo",
  firm: "Firma",
  service: "Servis",
  sales: "Plasiyer",
  production: "Üretim",
};

// Buton id'si ("storage") ile API/persist değeri ("Depo") FARKLI alfabelerdir.
// Filtre sessionStorage'a API değeriyle yazılıyor (aşağıda statusParam), ama
// aktif buton id ile eşleştiriliyor. Sayfaya geri dönüldüğünde state'i ham
// haliyle geri yüklemek hiçbir butonla eşleşmiyor ve filtre SEÇİMSİZ görünüyordu
// (veri doğru filtreleniyordu, yalnızca buton işaretsizdi). Bu yüzden geri
// yüklemede her zaman id'ye normalize edilir.
const STATUS_ID_BY_NAME = Object.fromEntries(
  Object.entries(STATUS_NAME_MAP).map(([id, name]) => [name, id]),
);

const toFilterId = (value) => {
  if (!value) return "all";
  if (STATUS_NAME_MAP[value]) return value; // zaten buton id'si
  return STATUS_ID_BY_NAME[value] || "all"; // "Depo" -> "storage"
};

const TABLE_COLUMNS = [
  { label: "CİHAZ BİLGİLERİ", width: "w-[16%]", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "BİLEŞENLER", width: "w-[14%]", skeletonWidth: "w-28", skeletonExtra: true },
  { label: "DURUM", width: "w-[11%]", skeletonWidth: "w-20", skeletonExtra: true },
  { label: "VERSİYONLAR", width: "w-[10%]", skeletonWidth: "w-20", skeletonExtra: true },
  { label: "KOTALAR", width: "w-[13%]", skeletonWidth: "w-20", skeletonExtra: true },
  { label: "FİRMA", width: "w-[14%]", skeletonWidth: "w-20" },
  { label: "AKSİYONLAR", width: "w-[12%]", skeletonWidth: "w-16" },
];

const ROW_HEIGHT_PX = 72;
const TABLE_HEADER_HEIGHT_PX = 48;

function DevicesList({
  devicesData = [],
  deviceDetailIsLoading,
  deviceDetailIsFetching,
  onDeviceClick = null,
  deviceRevised,
  operationHandler = null,
  setOperationHandler = null,
  selectedOperationDevice,
  setSelectedOperationDevice,
  searchParams = {},
  onSearchParamsChange,
  onPageChange,
  pagination,
  getDeviceDetail,
  authToken,
  isDealer = false,
}) {
  const { auth } = useAuth();
  const token = authToken || auth.accessToken;
  const { hasAnyPermission, hasPermission } = usePermission();
  const hasAnyActionPermission = hasAnyPermission(
    "devices.toggleActivity",
    "devices.moveToFirm",
    "devices.moveToService",
    "devices.moveToPlaymaker",
    "devices.moveToStorage",
  ) ||
    // Manuel kota (#178) ve Son İşlem (#180) yalnız merkezde — bayide eski
    // şablonda izin kalmış olabilir.
    (!isDealer && hasPermission("devices.quota")) ||
    (!isDealer && hasPermission("devices.lastOperation"));

  // Versiyon sütunu devices.infoVersion'a bağlı — cihaz detayındaki versiyon
  // kartıyla aynı yetki. Sütun başlığı ile satır hücresi AYNI koşulla
  // gizlenmeli (hücre: serviceDevicesList/DeviceCard), aksi halde tablo kayar.
  const canSeeVersions = hasPermission("devices.infoVersion");

  const columns = (hasAnyActionPermission
    ? TABLE_COLUMNS
    : TABLE_COLUMNS.filter((col) => col.label !== "AKSİYONLAR")
  ).filter((col) => canSeeVersions || col.label !== "VERSİYONLAR");

  // Local search state'leri (debounce için)
  const [localSerialNo, setLocalSerialNo] = useState(searchParams.serialNo || "");
  const [localDeviceName, setLocalDeviceName] = useState(searchParams.deviceName || "");
  const [localFirmName, setLocalFirmName] = useState(searchParams.firmName || "");
  const [localEyeCount, setLocalEyeCount] = useState(searchParams.cupNumber ?? "");

  const debouncedSerialNo = useDebounce(localSerialNo, 500);
  const debouncedDeviceName = useDebounce(localDeviceName, 500);
  const debouncedFirmName = useDebounce(localFirmName, 500);
  const debouncedEyeCount = useDebounce(localEyeCount, 500);
  const debouncedPage = useDebounce(searchParams.page || 1, 300);

  const [isSearching, setIsSearching] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [activeFilter, setActiveFilter] = useState(() =>
    toFilterId(
      isDealer
        ? searchParams.dealerDeviceStatusName
        : searchParams.deviceStatusName,
    ),
  );

  const currentFilterButtons = isDealer ? DEALER_FILTER_BUTTONS : FILTER_BUTTONS;

  const [openActionsDeviceId, setOpenActionsDeviceId] = useState(null);

  const [processingDeviceIds, setProcessingDeviceIds] = useState({
    moveToStorage: {},
    moveToProduction: {},
    resetQuota: {},
  });

  // API Mutations — dealer/storage'a göre doğru endpoint seçilir
  const [storageToggle, storageToggleStatus] = useStorageToggleMutation();
  const [dealerToggle, dealerToggleStatus] = useDealerToggleDeviceActivityMutation();
  const toggleDeviceActivity = isDealer ? dealerToggle : storageToggle;
  const toggleActivityStatus = isDealer ? dealerToggleStatus : storageToggleStatus;

  const [storageMoveToFirm, storageFirmStatus] = useStorageMoveToFirmMutation();
  const [dealerMoveToFirm, dealerFirmStatus] = useDealerMoveDeviceToFirmMutation();
  const moveDeviceToFirm = isDealer ? dealerMoveToFirm : storageMoveToFirm;
  const firmMoveStatus = isDealer ? dealerFirmStatus : storageFirmStatus;

  const [storageMoveToPlaymaker, storagePlaymakerStatus] = useStorageMoveToPlaymakerMutation();
  const [dealerMoveToPlaymaker, dealerPlaymakerStatus] = useDealerMoveDeviceToPlaymakerMutation();
  const moveDeviceToPlaymaker = isDealer ? dealerMoveToPlaymaker : storageMoveToPlaymaker;
  const playmakerMoveStatus = isDealer ? dealerPlaymakerStatus : storagePlaymakerStatus;

  const [storageMoveToService, storageServiceStatus] = useStorageMoveToServiceMutation();
  const [dealerMoveToService, dealerServiceStatus] = useDealerMoveDeviceToServiceMutation();
  const moveDeviceToService = isDealer ? dealerMoveToService : storageMoveToService;
  const serviceMoveStatus = isDealer ? dealerServiceStatus : storageServiceStatus;

  const [storageMoveToStorage, storageStorageStatus] = useStorageMoveToStorageMutation();
  const [dealerMoveToStorage, dealerStorageStatus] = useDealerMoveDeviceToStorageMutation();
  const moveDeviceToStorage = isDealer ? dealerMoveToStorage : storageMoveToStorage;
  const storageMoveStatus = isDealer ? dealerStorageStatus : storageStorageStatus;

  const [resetOperationQuota, quotaResetStatus] = useResetOperationQuotaMutation();
  const [moveDeviceToProduction, productionMoveStatus] = useMoveDeviceToProductionMutation();

  // Plasiyer uçları izin ister (merkez users.read, bayi devices.moveToPlaymaker)
  // — izni yoksa istek atılmaz (#414)
  const { data: playmakerData } = useFetchPlayMakerQuery(token, {
    skip: isDealer || !hasPermission("users.read"),
  });
  const { data: dealerPlaymakerData } = useFetchDealerPlayMakerQuery(token, {
    skip: !isDealer || !hasPermission("devices.moveToPlaymaker"),
  });
  const activePlaymakerData = isDealer ? dealerPlaymakerData : playmakerData;
  const { firms } = useFirmData(token);
  const hasSubDealers = isDealer && dealerHasSubDealers(auth, firms);

  // OperationDrawer state'leri
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDevice, setDrawerDevice] = useState(null);
  const [drawerOperation, setDrawerOperation] = useState(null);
  // "Firma" işleminde hedef türü: bayide Alt Firmaya Ver (customer) /
  // Alt Bayiye Ver (dealer) aynı uca gider, yalnız firma listesi değişir (#247).
  const [drawerFirmTarget, setDrawerFirmTarget] = useState("customer");
  // Merkezden bayi adına taşıma (#329) — kapanış kayması boyunca cihaz/mod
  // korunur, yalnız open düşer.
  const [centerDealerMove, setCenterDealerMove] = useState({ open: false });
  // Bayi cihazına Kota Ver / Kota Al (#335) — aynı kapanış kuralı
  const [centerDealerQuota, setCenterDealerQuota] = useState({ open: false });

  // Onay penceresi state'leri
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [connectDuringOperation, setConnectDuringOperation] = useState(true);

  const [alerts, setAlerts] = useState([]);
  const lastQueryRef = useRef("");
  const lastContentKeyRef = useRef("");
  const tableContainerRef = useRef(null);
  const lastAutoLimitRef = useRef(null);
  const getDeviceDetailRef = useRef(getDeviceDetail);
  const onSearchParamsChangeRef = useRef(onSearchParamsChange);
  getDeviceDetailRef.current = getDeviceDetail;
  // Her başarılı işlemden sonra listenin son sorgusu (aynı filtre + sayfa)
  // sunucudan yeniden çekilir (#260): Kota Dağıt firmanın tüm cihazlarını,
  // taşıma filtre üyeliğini ve sayaçları değiştirir — tek cihazı yanıttaki
  // değerle değiştirmek (deviceRevised) yetmiyordu.
  const refreshList = () => {
    const fetchList = getDeviceDetailRef.current;
    if (!fetchList || !lastQueryRef.current) return;
    try {
      fetchList({ ...JSON.parse(lastQueryRef.current), token });
    } catch {
      // lastQueryRef her zaman JSON.stringify ile yazılır; yine de liste bozulmasın
    }
  };
  onSearchParamsChangeRef.current = onSearchParamsChange;

  const hasCalculatedLimitRef = useRef(false);
  const [limitReady, setLimitReady] = useState(false);

  useEffect(() => {
    if (hasCalculatedLimitRef.current) return;
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      if (hasCalculatedLimitRef.current) return;
      const { height } = entries[0]?.contentRect ?? {};
      if (height < TABLE_HEADER_HEIGHT_PX + ROW_HEIGHT_PX) return;
      hasCalculatedLimitRef.current = true;
      ro.disconnect();
      const calculated = Math.max(1, Math.min(100, Math.floor((height - TABLE_HEADER_HEIGHT_PX) / ROW_HEIGHT_PX)));
      if (calculated === lastAutoLimitRef.current) return;
      lastAutoLimitRef.current = calculated;
      onSearchParamsChangeRef.current?.({ limit: calculated, page: 1 });
      setLimitReady(true);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!hasCalculatedLimitRef.current) setLimitReady(true);
    }, 400);
    return () => clearTimeout(t);
  }, []);

  const activeFilterLabel = currentFilterButtons.find((b) => b.id === activeFilter)?.label || "";
  const emptyStateMessage =
    activeFilter === "all" || !activeFilterLabel
      ? "Cihaz bulunamadı"
      : `${activeFilterLabel} durumunda cihaz bulunamadı`;

  // --- Alert & operation result watchers ---
  const displayAlert = (severity, message) => {
    const newAlert = { id: uuidv4(), severity, message, timestamp: new Date() };
    setAlerts((prev) => [...prev, newAlert]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id));
    }, 5000);
  };

  useEffect(() => {
    if (productionMoveStatus.isSuccess || productionMoveStatus.isError) {
      const deviceId = Object.keys(processingDeviceIds.moveToProduction)[0];
      if (deviceId) {
        const device = devicesData.find((d) => d._id === deviceId || d.id === deviceId);
        const deviceName = getDeviceDisplayName(device);
        const serialNo = device?.serialNo || "Bilinmeyen";
        displayAlert(
          productionMoveStatus.isSuccess ? "success" : "error",
          productionMoveStatus.isSuccess
            ? `"${deviceName}" (${serialNo}) cihazı başarıyla üretime alındı.`
            : `"${deviceName}" (${serialNo}) - ${productionMoveStatus.error?.data?.message || "Cihazı üretime alırken bir hata oluştu."}`
        );
        if (productionMoveStatus.isSuccess && deviceRevised && productionMoveStatus.data?.data?.device) {
          deviceRevised(productionMoveStatus.data.data.device);
        }
        if (productionMoveStatus.isSuccess) refreshList();
        setProcessingDeviceIds((prev) => {
          const n = { ...prev };
          delete n.moveToProduction[deviceId];
          return n;
        });
      }
    }
    if (storageMoveStatus.isSuccess || storageMoveStatus.isError) {
      const deviceId = Object.keys(processingDeviceIds.moveToStorage)[0];
      if (deviceId) {
        const device = devicesData.find((d) => d._id === deviceId || d.id === deviceId);
        const deviceName = getDeviceDisplayName(device);
        const serialNo = device?.serialNo || "Bilinmeyen";
        displayAlert(
          storageMoveStatus.isSuccess ? "success" : "error",
          storageMoveStatus.isSuccess
            ? `"${deviceName}" (${serialNo}) cihazı başarıyla depoya alındı.`
            : `"${deviceName}" (${serialNo}) - ${storageMoveStatus.error?.data?.message || "Cihazı depoya alırken bir hata oluştu."}`
        );
        if (storageMoveStatus.isSuccess && deviceRevised && storageMoveStatus.data?.data?.device) {
          deviceRevised(storageMoveStatus.data.data.device);
        }
        if (storageMoveStatus.isSuccess) refreshList();
        setProcessingDeviceIds((prev) => {
          const n = { ...prev };
          delete n.moveToStorage[deviceId];
          return n;
        });
      }
    }
    if (quotaResetStatus.isSuccess || quotaResetStatus.isError) {
      const deviceId = Object.keys(processingDeviceIds.resetQuota)[0];
      if (deviceId) {
        const device = devicesData.find((d) => d._id === deviceId || d.id === deviceId);
        const deviceName = getDeviceDisplayName(device);
        const serialNo = device?.serialNo || "Bilinmeyen";
        displayAlert(
          quotaResetStatus.isSuccess ? "success" : "error",
          quotaResetStatus.isSuccess
            ? `"${deviceName}" (${serialNo}) cihazının kotası başarıyla yenilendi.`
            : `"${deviceName}" (${serialNo}) - ${quotaResetStatus.error?.data?.message || "Cihazın kotasını yenilerken bir hata oluştu."}`
        );
        if (quotaResetStatus.isSuccess) refreshList();
        setProcessingDeviceIds((prev) => {
          const n = { ...prev };
          delete n.resetQuota[deviceId];
          return n;
        });
      }
    }
  }, [
    storageMoveStatus.isSuccess, storageMoveStatus.isError, storageMoveStatus.error,
    quotaResetStatus.isSuccess, quotaResetStatus.isError, quotaResetStatus.error,
    processingDeviceIds, devicesData, deviceRevised,
    productionMoveStatus.isSuccess, productionMoveStatus.isError, productionMoveStatus.error,
  ]);

  // --- Confirm / actions ---
  const showConfirm = (device, action, title, message, callback) => {
    setConfirmAction(action);
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmCallback(() => callback);
    setConnectDuringOperation(true);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (confirmCallback && typeof confirmCallback === "function") {
      if (confirmAction === "moveToStorage" || confirmAction === "moveToProduction") {
        confirmCallback(connectDuringOperation);
      } else {
        confirmCallback();
      }
    }
    setShowConfirmModal(false);
  };

  // --- Search / filter API integration ---
  useEffect(() => {
    const getDeviceDetailFn = getDeviceDetailRef.current;
    const onSearchParamsChangeFn = onSearchParamsChangeRef.current;
    if (!getDeviceDetailFn || !token) return;

    if (hasTyped) setIsSearching(true);

    const normalize = (v) => (typeof v === "string" ? v.trim().toLowerCase() : v);
    const statusValue = activeFilter === "all" ? undefined : STATUS_NAME_MAP[activeFilter] || activeFilter;
    const cupNumberRaw = debouncedEyeCount !== "" ? Number(debouncedEyeCount) : NaN;
    const cupNumberParam = !Number.isNaN(cupNumberRaw) ? cupNumberRaw : undefined;

    const contentKey = JSON.stringify({
      serialNo: normalize(debouncedSerialNo),
      deviceName: normalize(debouncedDeviceName),
      firmName: normalize(debouncedFirmName),
      cupNumber: cupNumberParam,
      statusValue,
    });
    const filterOrSearchChanged = contentKey !== lastContentKeyRef.current;
    if (filterOrSearchChanged) lastContentKeyRef.current = contentKey;
    const pageToUse = filterOrSearchChanged ? 1 : debouncedPage;

    const statusParam = isDealer
      ? { dealerDeviceStatusName: statusValue }
      : { deviceStatusName: statusValue };

    const params = {
      serialNo: normalize(debouncedSerialNo),
      deviceName: normalize(debouncedDeviceName),
      firmName: normalize(debouncedFirmName),
      cupNumber: cupNumberParam,
      ...statusParam,
      page: pageToUse,
      limit: searchParams.limit ?? 20,
    };

    if (onSearchParamsChangeFn) onSearchParamsChangeFn({ ...params, page: pageToUse });

    const queryKey = JSON.stringify({ token, ...params });
    if (lastQueryRef.current === queryKey) {
      if (hasTyped) setIsSearching(false);
      return;
    }
    if (!limitReady) return;
    lastQueryRef.current = queryKey;
    getDeviceDetailFn({ token, ...params });
  }, [
    debouncedSerialNo, debouncedDeviceName, debouncedFirmName, debouncedEyeCount,
    activeFilter, debouncedPage, searchParams.limit, token, hasTyped, limitReady,
  ]);

  useEffect(() => {
    if (!deviceDetailIsLoading && !deviceDetailIsFetching && hasTyped) setIsSearching(false);
  }, [deviceDetailIsLoading, deviceDetailIsFetching, hasTyped]);

  const filteredDevices = devicesData;

  const handlePageChangeLocal = (page) => {
    if (onPageChange) onPageChange(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  };

  // ── Mobil sonsuz kaydırma ──────────────────────────────────────────
  // md altında sayfalama gizlenir; liste sonuna yaklaşınca sonraki sayfa
  // çekilir ve kartlar mevcut listenin altına eklenir.
  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobileView(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [mobileDevices, setMobileDevices] = useState([]);
  const currentPage = pagination?.currentPage || 1;
  const totalPages = pagination?.totalPages || 1;
  useEffect(() => {
    if (currentPage <= 1) {
      setMobileDevices(devicesData);
    } else {
      // Yeni sayfayı mevcutların altına ekle (id bazlı dedupe)
      setMobileDevices((prev) => {
        const seen = new Set(prev.map((d) => d._id || d.id));
        const fresh = (devicesData || []).filter((d) => !seen.has(d._id || d.id));
        return [...prev, ...fresh];
      });
    }
  }, [devicesData, currentPage]);

  const canLoadMore = currentPage < totalPages;
  // Sayfa 2+ yüklenirken tam skeleton yerine liste altında küçük spinner
  const isAppendLoading =
    isMobileView && currentPage > 1 && mobileDevices.length > 0;
  const loadMoreRef = useRef(null);
  // isLoading bu satırın altında tanımlanıyor; observer güncel değeri ref'ten okur
  const isLoadingRef = useRef(false);
  useEffect(() => {
    if (!isMobileView) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingRef.current && canLoadMore) {
          onPageChange?.(currentPage + 1);
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileView, canLoadMore, currentPage, mobileDevices.length]);

  // Firma kota dağıtımı — cihazın firması için (firma cihazlar sayfasındaki "Kota Dağıt"
  // ile aynı uç ve aynı meşgul kaydı: sürerken o firmanın cihazları her yerde meşgul).
  const { isBusy: isFirmQuotaBusy, distribute: distributeFirmQuota } = useFirmQuotaDistribution();
  const handleDistributeQuota = (device) => {
    const firm = device?.firm;
    if (!firm?._id || !firm.firmName) return;
    showConfirm(device, "distributeQuota", "Kota Dağıt",
      `"${firm.firmName}" firmasının dağıtılmamış kotası, firmanın kota almaya açık ve kullanıma açık cihazlarına eşit dağıtılacak. Emin misiniz?`,
      async () => {
        try {
          const done = await distributeFirmQuota(firm);
          if (done) {
            displayAlert("success", `"${firm.firmName}" için kota dağıtımı tamamlandı.`);
            refreshList();
          }
        } catch (err) {
          displayAlert("error", err?.data?.message || "Kota dağıtımı sırasında bir hata oluştu.");
        }
      });
  };

  const handleResetQuota = (device) => {
    setProcessingDeviceIds((prev) => ({
      ...prev,
      resetQuota: { ...prev.resetQuota, [device._id]: true },
    }));
    resetOperationQuota({ id: device._id, token });
  };

  // --- OperationDrawer handlers ---
  const handleOpenOperationDrawer = (device, operation, opts = {}) => {
    setDrawerDevice(device);
    setDrawerOperation(operation);
    setDrawerFirmTarget(opts.firmTarget || "customer");
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerDevice(null);
    setDrawerOperation(null);
    toggleActivityStatus.reset();
    playmakerMoveStatus.reset();
    serviceMoveStatus.reset();
    firmMoveStatus.reset();
    storageMoveStatus.reset();
    productionMoveStatus.reset();
  };

  const handleDrawerConfirm = (operationData) => {
    const { device, connect, description, selectedPlaymakerId, firmName, isActive } = operationData;
    if (drawerOperation === "Depo") {
      setProcessingDeviceIds((prev) => ({ ...prev, moveToStorage: { ...prev.moveToStorage, [device._id]: true } }));
      moveDeviceToStorage({ id: device._id, connect, description, token });
    } else if (drawerOperation === "Üretim") {
      setProcessingDeviceIds((prev) => ({ ...prev, moveToProduction: { ...prev.moveToProduction, [device._id]: true } }));
      moveDeviceToProduction({ id: device._id, connect, description, token });
    } else if (drawerOperation === "AktiflikKapat") {
      toggleDeviceActivity({ token, id: device._id, connect, description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE, isActive: false });
    } else if (drawerOperation === "AktiflikAc") {
      toggleDeviceActivity({ token, id: device._id, connect, description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN, isActive: true });
    } else if (drawerOperation === "Plasiyer") {
      if (!selectedPlaymakerId) return;
      moveDeviceToPlaymaker({ token, id: device._id, connect, userID: selectedPlaymakerId, description: description || "Cihaz plasiyere verildi." });
    } else if (drawerOperation === "Servis") {
      moveDeviceToService({ token, id: device._id, connect, description: description || "Cihaz servise alındı.", isActive: isActive ?? true });
    } else if (drawerOperation === "Firma") {
      if (!firmName) return;
      moveDeviceToFirm({ token, id: device._id, connect, firmName, description: description || "", isActive: isActive || false });
    }
  };

  useEffect(() => {
    if (!drawerDevice) return;
    const entries = [
      { status: toggleActivityStatus, label: "kota alma" },
      { status: playmakerMoveStatus, label: "plasiyere verme" },
      { status: serviceMoveStatus, label: "servise alma" },
      { status: firmMoveStatus, label: "firmaya alma" },
    ];
    for (const { status, label } of entries) {
      if (status.isSuccess || status.isError) {
        const deviceName = getDeviceDisplayName(drawerDevice);
        const serialNo = drawerDevice?.serialNo || "Bilinmeyen";
        displayAlert(
          status.isSuccess ? "success" : "error",
          status.isSuccess
            ? `"${deviceName}" (${serialNo}) üzerindeki ${label} işlemi başarıyla tamamlandı.`
            : `"${deviceName}" (${serialNo}) - ${status.error?.data?.message || "İşlem sırasında bir hata oluştu."}`
        );
        if (status.isSuccess && deviceRevised && status.data?.data?.device) {
          deviceRevised(status.data.data.device);
        }
        if (status.isSuccess) refreshList();
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toggleActivityStatus.isSuccess, toggleActivityStatus.isError,
    playmakerMoveStatus.isSuccess, playmakerMoveStatus.isError,
    serviceMoveStatus.isSuccess, serviceMoveStatus.isError,
    firmMoveStatus.isSuccess, firmMoveStatus.isError,
  ]);

  // --- Search input change handler ---
  const handleSearchChange = (setter) => (e) => {
    setter(e.target.value);
    setHasTyped(true);
    setIsSearching(true);
  };

  // --- Render helpers ---
  const renderDeviceCard = (device, index, isMobileView) => (
    <DeviceCard
      key={device.id || device._id || index}
      device={device}
      token={token}
      onDeviceClick={onDeviceClick}
      onOpenOperationDrawer={handleOpenOperationDrawer}
      onOpenCenterDealerMove={isDealer ? undefined : (d, mode) => setCenterDealerMove({ open: true, device: d, mode })}
      onOpenCenterDealerQuota={isDealer ? undefined : (d, direction) => setCenterDealerQuota({ open: true, device: d, direction })}
      onResetQuota={(d) => {
        showConfirm(d, "refreshQuota", "Kotasını Yenile",
          `"${getDeviceDisplayName(d)}" cihazının kotasını yenilemek istediğinize emin misiniz?${!isMobileView ? " Bu işlem cihazın veri kotasını sıfırlayacaktır." : ""}`,
          () => handleResetQuota(d));
      }}
      isMoveToProductionLoading={!!processingDeviceIds.moveToProduction[device._id]}
      isMoveToStorageLoading={!!processingDeviceIds.moveToStorage[device._id]}
      isResetQuotaLoading={!!processingDeviceIds.resetQuota[device._id]}
      onDistributeQuota={isDealer ? undefined : handleDistributeQuota}
      isFirmQuotaBusy={isFirmQuotaBusy(device.firm?._id)}
      hasSubDealers={hasSubDealers}
      operationHandler={operationHandler}
      setOperationHandler={setOperationHandler}
      selectedOperationDevice={selectedOperationDevice}
      setSelectedOperationDevice={setSelectedOperationDevice}
      isMobileView={isMobileView}
      openActionsDeviceId={openActionsDeviceId}
      setOpenActionsDeviceId={setOpenActionsDeviceId}
      isDealer={isDealer}
    />
  );

  const isLoading = deviceDetailIsLoading || isSearching;
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  return (
    <div className="flex flex-col h-full" onClick={() => openActionsDeviceId && setOpenActionsDeviceId(null)}>
      {/* Alerts */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[99999] w-3/4 max-w-xl flex flex-col gap-2 pointer-events-none [&>*]:pointer-events-auto">
        {alerts.map((a) => (
          <Alert
            key={a.id}
            severity={a.severity}
            onClose={() => setAlerts((prev) => prev.filter((x) => x.id !== a.id))}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", width: "100%", animation: "slideInDown 0.3s ease-out" }}
          >
            {a.message}
          </Alert>
        ))}
      </div>

      <ConfirmationModal
        open={showConfirmModal}
        title={confirmTitle}
        message={confirmMessage}
        action={confirmAction}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        connectDuringOperation={connectDuringOperation}
        setConnectDuringOperation={setConnectDuringOperation}
      />

      {!isDealer && (
        <CenterDealerMoveDrawer
          open={centerDealerMove.open}
          device={centerDealerMove.device}
          mode={centerDealerMove.mode}
          onClose={() => setCenterDealerMove((prev) => ({ ...prev, open: false }))}
          onDone={(updated) => {
            if (deviceRevised) deviceRevised(updated);
            refreshList();
          }}
        />
      )}
      {!isDealer && (
        <CenterDealerQuotaDrawer
          open={centerDealerQuota.open}
          device={centerDealerQuota.device}
          direction={centerDealerQuota.direction}
          onClose={() => setCenterDealerQuota((prev) => ({ ...prev, open: false }))}
          onDone={refreshList}
        />
      )}

      <OperationDrawer
        open={drawerOpen}
        device={drawerDevice}
        operation={drawerOperation}
        onClose={handleDrawerClose}
        onConfirm={handleDrawerConfirm}
        playmakers={activePlaymakerData?.data?.playmakers || []}
        firms={firms || []}
        firmTarget={drawerFirmTarget}
        isLoading={
          (drawerOperation === "Depo" && storageMoveStatus.isLoading) ||
          (drawerOperation === "Üretim" && productionMoveStatus.isLoading) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && toggleActivityStatus.isLoading) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isLoading) ||
          (drawerOperation === "Servis" && serviceMoveStatus.isLoading) ||
          (drawerOperation === "Firma" && firmMoveStatus.isLoading)
        }
        isSuccess={
          (drawerOperation === "Depo" && storageMoveStatus.isSuccess) ||
          (drawerOperation === "Üretim" && productionMoveStatus.isSuccess) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && toggleActivityStatus.isSuccess) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isSuccess) ||
          (drawerOperation === "Servis" && serviceMoveStatus.isSuccess) ||
          (drawerOperation === "Firma" && firmMoveStatus.isSuccess)
        }
        isError={
          (drawerOperation === "Depo" && storageMoveStatus.isError) ||
          (drawerOperation === "Üretim" && productionMoveStatus.isError) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && toggleActivityStatus.isError) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isError) ||
          (drawerOperation === "Servis" && serviceMoveStatus.isError) ||
          (drawerOperation === "Firma" && firmMoveStatus.isError)
        }
        errorMessage={
          storageMoveStatus.error?.data?.message ||
          productionMoveStatus.error?.data?.message ||
          toggleActivityStatus.error?.data?.message ||
          playmakerMoveStatus.error?.data?.message ||
          serviceMoveStatus.error?.data?.message ||
          firmMoveStatus.error?.data?.message || ""
        }
      />

      {/* Device List */}
      <div className="bg-white h-full flex flex-col">
        {/* Filters & Search */}
        <ListToolbar
          className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
          mobileFilterSlotId="devices-toolbar-mobile-slot"
          filterTabs={{
            filters: currentFilterButtons,
            activeFilter,
            onChange: setActiveFilter,
            totalCount: pagination?.totalDevices || 0,
            isLoading,
            color: "indigo",
          }}
          searchFields={[
            { placeholder: "Seri No", value: localSerialNo, onChange: handleSearchChange(setLocalSerialNo) },
            { placeholder: "Firma İsmi", value: localFirmName, onChange: handleSearchChange(setLocalFirmName) },
            {
              placeholder: "Model İsmi",
              value: localDeviceName,
              onChange: handleSearchChange(setLocalDeviceName),
              hideOnMobile: true,
            },
            {
              placeholder: "Göz Sayısı",
              value: localEyeCount,
              onChange: handleSearchChange(setLocalEyeCount),
              type: "number",
              min: "0",
              hideOnMobile: true,
            },
          ]}
          searchNote={
            hasTyped && isLoading ? (
              <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-indigo-600">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span>Aranıyor...</span>
              </div>
            ) : null
          }
        />

        {/* Table Content */}
        <ListView
          contentRef={tableContainerRef}
          headers={columns.map((col) => ({ label: col.label, className: col.width }))}
          data={isMobileView ? mobileDevices : filteredDevices}
          rowKey={(device, i) => device.id || device._id || i}
          renderRow={(device, i) => renderDeviceCard(device, i, false)}
          renderMobileCard={(device, i) => renderDeviceCard(device, i, true)}
          isLoading={isLoading && !isAppendLoading}
          mobileListFooter={
            <div>
              <div ref={loadMoreRef} className="h-1" />
              {isAppendLoading && isLoading && (
                <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  Yükleniyor...
                </div>
              )}
              {!canLoadMore && mobileDevices.length > 0 && (
                <div className="py-3 text-center text-xs text-gray-400">
                  Tüm cihazlar yüklendi
                </div>
              )}
            </div>
          }
          loadingNode={
            <ListSkeleton
              columns={columns}
              rowCount={pagination?.limit || 20}
              mobileCardCount={5}
            />
          }
          empty={
            <div className="p-4 md:p-6">
              <EmptyState
                message={emptyStateMessage}
                suggestion="Farklı arama terimleri veya filtreler deneyin"
              />
            </div>
          }
          footer={
            pagination && (pagination.totalPages > 1 || filteredDevices.length > 0) ? (
              <div className="shrink-0 border-t border-gray-200 bg-white hidden md:block">
                <Pagination
                  currentPage={pagination.currentPage || 1}
                  totalPages={pagination.totalPages || 1}
                  onPageChange={handlePageChangeLocal}
                  itemsPerPage={pagination.limit || 10}
                  totalItems={pagination.totalDevices || 0}
                  showInfo={true}
                  disabled={isLoading}
                />
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}

export default DevicesList;
